import {
	IconDarkLightOutline18,
	IconFingerprintOutline18,
	IconLanguageOutline18,
	IconLinkOutline18 as Link,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { scale } from "@productlane/lib/src/plans-client"
import { Switch, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { GoogleAnalyticsSettings } from "@/components/shared/google-analytics-settings"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { useTrial } from "@/lib/useTrial"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { PortalInstancesSection } from "../portal-instances-section"
import { PortalPreview } from "../portal-preview"
import { SettingsHeader } from "../settings-header"
import { SettingItem } from "../settings-switches/setting-item"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "../settings-switches/settings-container"
import { settingsNavGroupClass, SettingsNavRow } from "./settings-nav-row"

export function PortalSettingsPage() {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)
	const { isTrial, isStartupProgram } = useTrial()

	if (!workspace) return null

	const hasAnyPortalEnabled =
		workspace.changelogPublic === true ||
		workspace.helpCenterPublic === true ||
		workspace.domainPublic === true ||
		workspace.customerPortalSettings?.isEnabled === true

	const previewSettingsHash = JSON.stringify({
		changelogPublic: workspace.changelogPublic,
		helpCenterPublic: workspace.helpCenterPublic,
		domainPublic: workspace.domainPublic,
		customerPortalEnabled: workspace.customerPortalSettings?.isEnabled,
		hideBranding: workspace.hideBranding,
		accentColor: workspace.accentColor,
		darkModeAccentColor: workspace.darkModeAccentColor,
		buttonTextColor: workspace.buttonTextColor,
		darkModeButtonTextColor: workspace.darkModeButtonTextColor,
		portalEmbedImageUrl: workspace.portalEmbedImageUrl,
		logoUrl: workspace.logoUrl,
		name: workspace.name,
	})

	return (
		<>
			<MetaInfo title="Portal Settings" />

			<SettingsHeader
				title="Portal"
				subtitle="Manage general settings for your docs, changelog and requests portal."
				// change this to actual portal page once it published
				// action={
				// <HowItWorksButton article=""/>
				// }
			/>

			<SettingsCardInsetContext.Provider value={true}>
				<div className="flex flex-col gap-8 lg:flex-row lg:justify-between">
					<div className="min-w-0 flex-1 lg:max-w-2xl">
						<PortalInstancesSection />

						<div className={cn(settingsNavGroupClass, "mb-4 mt-4")}>
							<SettingsNavRow
								icon={IconDarkLightOutline18}
								title="Theme"
								description="Adjust the cover image and colors of your portal and widget."
								link="/settings/portal/theme"
							/>
							<SettingsNavRow
								icon={Link}
								title="Custom domain"
								description="Connect your own domain to your portal."
								link="/settings/portal/domain"
							/>
							<SettingsNavRow
								icon={IconFingerprintOutline18}
								title="SSO"
								description="Authenticate users so they are logged in automatically."
								link="/settings/portal/sso"
							/>
						</div>

						<div className={settingsNavGroupClass}>
							<SettingsNavRow
								icon={IconLanguageOutline18}
								title="Language"
								description="Enable multiple languages for your portal and widget."
								link="/settings/languages"
							/>
						</div>

						<SettingsContainer className="mt-4">
							<GoogleAnalyticsSettings workspace={workspace} />

							<SettingItem
								title="Branding"
								className="pt-4"
								description="Show Productlane branding on the Portal."
								action={
									<>
										{!hasRequiredPlan ? (
											<UpgradeButton required={["scale"]} />
										) : isStartupProgram ? (
											<Tooltip label="The Startup program keeps the Productlane branding. Move to the full Scale plan to remove it.">
												<Switch
													name="branding"
													label={null}
													className="pr-1"
													checked
													disabled
												/>
											</Tooltip>
										) : isTrial ? (
											<Tooltip label="Productlane branding can only be removed on a paid plan, not during the trial.">
												<Switch
													name="branding"
													label={null}
													className="pr-1"
													checked
													disabled
												/>
											</Tooltip>
										) : (
											<Switch
												name="branding"
												label={null}
												className="pr-1"
												checked={!workspace.hideBranding} // inverted to keep logic
												onCheckedChange={(value) => {
													zero.mutate(
														mutators.workspaces.update({
															id: workspace.id,
															hideBranding: !value,
														}),
													)
													analytics.track("toggle-branding", {
														value: !value,
													})
												}}
											/>
										)}
									</>
								}
							/>
						</SettingsContainer>
					</div>
					<div className="w-full lg:w-[420px] lg:flex-shrink-0">
						<PortalPreview
							workspace={workspace}
							settingsHash={previewSettingsHash}
							hasAnyPortalEnabled={hasAnyPortalEnabled}
						/>
					</div>
				</div>
			</SettingsCardInsetContext.Provider>
		</>
	)
}
